// File: src/components/pages/payments/ApprovalList.jsx
// List of claimed payments with approve/reject actions

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Calendar,
  Hash,
  User,
  CreditCard,
  Package
} from 'lucide-react';
import { paymentAPI } from '../../../services/api';
import PaymentMappingTab from './PaymentMappingTab';
import '../../../styles/unified.css';

const ApprovalList = ({ refreshTrigger, onApprovalAction }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('claimed'); // 'claimed' | 'mapping'

  useEffect(() => {
    loadClaimedPayments();
  }, [refreshTrigger]);

  const loadClaimedPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const claimedPayments = await paymentAPI.getPaymentsByStatus('Claimed', getAccessTokenSilently);
      
      // Sort by payment date descending
      const sorted = claimedPayments.sort((a, b) => 
        new Date(b.paymentDate) - new Date(a.paymentDate)
      );

      setPayments(sorted);
      setLoading(false);
    } catch (err) {
      console.error('Error loading claimed payments:', err);
      setError('Failed to load payments for approval');
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId) => {
    if (!window.confirm('Are you sure you want to approve this payment?')) {
      return;
    }

    try {
      setActionLoading(paymentId);
      await paymentAPI.approvePayment(paymentId, getAccessTokenSilently);
      
      // Remove from list
      setPayments(prev => prev.filter(p => p.paymentId !== paymentId));
      
      onApprovalAction();
      setActionLoading(null);
    } catch (err) {
      console.error('Error approving payment:', err);
      alert('Failed to approve payment');
      setActionLoading(null);
    }
  };

  const handleReject = async (paymentId) => {
    if (!window.confirm('Are you sure you want to reject this payment? This action cannot be undone and the record will be deleted.')) {
      return;
    }

    try {
      setActionLoading(paymentId);
      await paymentAPI.rejectPayment(paymentId, getAccessTokenSilently);
      
      // Remove from list
      setPayments(prev => prev.filter(p => p.paymentId !== paymentId));
      
      onApprovalAction();
      setActionLoading(null);
    } catch (err) {
      console.error('Error rejecting payment:', err);
      alert('Failed to reject payment');
      setActionLoading(null);
    }
  };

  // ── Tab bar ─────────────────────────────────────────────────────────────────
  const tabBar = (
    <div className="left-tab-bar" style={{ marginBottom: '1rem' }}>
      <button
        className={`left-tab ${activeTab === 'claimed' ? 'left-tab-active' : ''}`}
        onClick={() => setActiveTab('claimed')}
      >
        Claimed Payments
        {!loading && payments.length > 0 && (
          <span className="left-tab-badge left-tab-badge-private">{payments.length}</span>
        )}
      </button>
      <button
        className={`left-tab ${activeTab === 'mapping' ? 'left-tab-active left-tab-exceptional' : ''}`}
        onClick={() => setActiveTab('mapping')}
      >
        Confirmed Payments
      </button>
    </div>
  );

  if (activeTab === 'mapping') {
    return (
      <div>
        {tabBar}
        <PaymentMappingTab onMappingComplete={onApprovalAction} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-icon">⏳</div>
        <h2>Loading payments...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-error-banner">
        <AlertCircle size={24} />
        <span>{error}</span>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="empty-state">
        <CheckCircle size={64} />
        <h3>No Payments Awaiting Approval</h3>
        <p>All claimed payments have been processed.</p>
      </div>
    );
  }

  return (
    <div>
    {tabBar}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Summary */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <div className="card-body" style={{ padding: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>
            {payments.length} Payment{payments.length !== 1 ? 's' : ''} Awaiting Approval
          </h3>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem' }}>
            Total Amount: £{payments.reduce((sum, p) => sum + p.paymentAmount, 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Payment List */}
      {payments.map(payment => (
        <div key={payment.paymentId} className="card">
          <div className="card-body">
            {/* Payment Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'flex-start',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Hash size={18} className="text-muted" />
                  <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    Serial #{payment.serialNumber}
                  </span>
                </div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>
                  {payment.studentName}
                </h3>
              </div>
              <div style={{ 
                padding: '0.5rem 1rem',
                background: '#fff3cd',
                color: '#856404',
                borderRadius: '1rem',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                Claimed
              </div>
            </div>

            {/* Payment Details Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <Calendar size={16} className="text-muted" />
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Payment Date</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#111827' }}>
                  {new Date(payment.paymentDate).toLocaleDateString('en-GB')}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <Package size={16} className="text-muted" />
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Payment Option</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#111827' }}>
                  {payment.paymentOption}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <CreditCard size={16} className="text-muted" />
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Payment Method</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#111827' }}>
                  {payment.paymentMethod}
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <User size={16} className="text-muted" />
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Number of Classes</span>
                </div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#111827' }}>
                  {payment.numberOfClasses || 'N/A'}
                </div>
              </div>
            </div>

            {/* Amount Breakdown */}
            <div style={{ 
              background: '#f9fafb',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: '#6b7280' }}>Original Amount:</span>
                <span style={{ fontWeight: 500 }}>£{payment.originalAmount.toFixed(2)}</span>
              </div>
              
              {payment.discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6b7280' }}>Discount ({payment.discountType}):</span>
                  <span style={{ color: '#10b981', fontWeight: 500 }}>
                    -£{payment.discountAmount.toFixed(2)}
                  </span>
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                paddingTop: '0.75rem',
                borderTop: '2px solid #e5e7eb',
                marginTop: '0.5rem'
              }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 600, color: '#111827' }}>
                  Final Amount:
                </span>
                <span style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  £{payment.paymentAmount.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Notes */}
            {payment.notes && (
              <div style={{ 
                padding: '0.75rem 1rem',
                background: '#eff6ff',
                borderLeft: '4px solid #2196f3',
                borderRadius: '0.25rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ fontSize: '0.85rem', color: '#1e40af', fontWeight: 500, marginBottom: '0.25rem' }}>
                  Notes:
                </div>
                <div style={{ fontSize: '0.9rem', color: '#1e40af' }}>
                  {payment.notes}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => handleReject(payment.paymentId)}
                disabled={actionLoading === payment.paymentId}
                className="btn-danger"
                style={{ minWidth: '120px' }}
              >
                <XCircle size={18} />
                {actionLoading === payment.paymentId ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                onClick={() => handleApprove(payment.paymentId)}
                disabled={actionLoading === payment.paymentId}
                className="btn-success"
                style={{ minWidth: '120px' }}
              >
                <CheckCircle size={18} />
                {actionLoading === payment.paymentId ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

export default ApprovalList;